import useEvent from "react-use-event-hook";
import { useRecoilCallback, useSetRecoilState } from "recoil";

import { useReportId } from "@ryde/redux";
import { reportFiltersWithValuesAF } from "@ryde/shared";

import { dashboardAppliedFiltersAtom } from "../dynamic-dashboard.atoms";

interface IUseDashboardFilters {
  applyFilters: () => Promise<void>;
  clearFilters: () => void;
}

/**
 * Apply / clear for the dashboard filter bar. Filters live in the reports'
 * shared filter atom (keyed by the dashboard's sentinel report id, 0); "apply"
 * snapshots the live values into `dashboardAppliedFiltersAtom` (which every
 * card's fetch reads), "clear" empties it. The snapshot read is a Recoil
 * callback so editing a filter doesn't re-render the whole grid.
 */
export const useDashboardFilters = (): IUseDashboardFilters => {
  const { reportId } = useReportId();
  const setAppliedFilters = useSetRecoilState(dashboardAppliedFiltersAtom);

  const applyFilters = useRecoilCallback(
    ({ snapshot, set }) =>
      async (): Promise<void> => {
        const live = await snapshot.getPromise(
          reportFiltersWithValuesAF(reportId),
        );
        set(dashboardAppliedFiltersAtom, { ...(live ?? {}) });
      },
    [reportId],
  );

  const clearFilters = useEvent((): void => setAppliedFilters({}));

  return { applyFilters, clearFilters };
};
